// src/features/analytics/analyticsTabs.ts
//
// Pure tab model for the Analytics section (unit-tested per ADR-004).
// AnalyticsLayout renders exactly what visibleAnalyticsTabs returns, so tab
// exposure rules live here and nowhere else.

import type { AnalyticsScopeKind } from '@/lib/constants/analytics';

export const TAB_PATHS = {
  overview: '/analytics',
  content: '/analytics/content',
  audience: '/analytics/audience',
  delivery: '/analytics/delivery',
  subaccounts: '/analytics/subaccounts',
} as const;

export type AnalyticsTab = keyof typeof TAB_PATHS;

export const TAB_LABELS: Record<AnalyticsTab, string> = {
  overview: 'Overview',
  content: 'Content',
  audience: 'Audience',
  delivery: 'Delivery',
  subaccounts: 'Sub-Accounts',
};

export function tabFromPath(pathname: string): AnalyticsTab {
  // The per-stream page (ADR-009) is a Content drill-in: keep that tab active.
  if (pathname.startsWith('/analytics/content') || pathname.startsWith('/analytics/media/')) return 'content';
  if (pathname.startsWith('/analytics/audience')) return 'audience';
  if (pathname.startsWith('/analytics/delivery')) return 'delivery';
  if (pathname.startsWith('/analytics/subaccounts')) return 'subaccounts';
  return 'overview';
}

/**
 * Tabs rendered for a scope kind + the delivery build flag. Audience is
 * account-only and Sub-Accounts network-only (the layout's scope bounce
 * mirrors this). Delivery ("CDN Delivery") additionally requires
 * FEATURE_FLAGS.analyticsDeliveryEnabled — staged dark at launch per
 * ADR-010 §3; router.tsx gates its route registration with the same flag.
 */
export function visibleAnalyticsTabs(
  scopeKind: AnalyticsScopeKind,
  deliveryEnabled: boolean
): AnalyticsTab[] {
  const tabs: AnalyticsTab[] = ['overview', 'content'];
  if (scopeKind === 'account') tabs.push('audience');
  if (deliveryEnabled) tabs.push('delivery');
  if (scopeKind === 'network') tabs.push('subaccounts');
  return tabs;
}
